import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { MeResponse, CurrentUser } from '../types/admin';

interface AuthState {
  loading: boolean;
  user: CurrentUser | null;
  permissions: Set<string>;
}

const AuthContext = createContext<AuthState>({ loading: true, user: null, permissions: new Set() });

/**
 * Fetches /auth/me exactly once on mount. No token handling of any
 * kind happens here - the existing httpOnly session cookie is what
 * actually authenticates every request; this context only reflects
 * what the server already knows, for UI purposes (showing the current
 * user's name, and conditionally rendering nav/buttons via
 * usePermission()). If not authenticated, redirects to the new
 * branded /login page (email-first router) rather than straight to
 * Entra - that page itself decides, per email, whether to route to
 * the existing server-side Entra flow or to a Partner account login.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, user: null, permissions: new Set() });

  useEffect(() => {
    fetch('/auth/me', { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((data: MeResponse) => {
        if (!data.authenticated || !data.user) {
          window.location.href = '/login';
          return;
        }
        setState({ loading: false, user: data.user, permissions: new Set(data.permissions ?? []) });
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * UX convenience only - hides/disables things the user's role
 * wouldn't be permitted to use anyway. The actual enforcement is,
 * and remains, the authorize() middleware + RBACEngine.can() on
 * every backend route. A component using this hook and getting it
 * wrong (or this list being stale) never grants access to anything -
 * worst case it shows a button that then returns a 403.
 */
export function usePermission(permissionKey: string): boolean {
  const { permissions } = useAuth();
  return permissions.has(permissionKey);
}
